// Worker base URL. The deployed backend handles all /api/* routes.
// In dev we still hit the deployed worker (no local mock).
export const WORKER_BASE_URL = 'https://tempo-backend.plewis-3e6.workers.dev';

// Bridge URL: same-origin from the admin SPA so the
// portal_session cookie (Domain=.cadenceadvisers.com) is sent.
// Wrangler binds admin.cadenceadvisers.com/api/portal/* to the worker.
export const PORTAL_BRIDGE_URL = 'https://admin.cadenceadvisers.com/api/portal/admin-bearer';

const SESSION_KEY = 'tempo_admin_session';

export type StoredSession = { token: string; email: string; expiresAt: string };

/**
 * Adopt an existing portal session (Task 038.7 bridge). If the user
 * authenticated at cadenceadvisers.com/portal/login, the resulting
 * cookie is scoped to .cadenceadvisers.com and reaches the bridge
 * endpoint on the admin subdomain. The endpoint mints a matching admin
 * Bearer; we save it locally so the existing auth flow continues
 * unchanged. Returns true if a session was adopted.
 *
 * Safe to call repeatedly; if there's already a localStorage session
 * we don't refetch.
 */
export async function tryAdoptPortalSession(): Promise<boolean> {
  if (loadSession()) return true;
  try {
    const r = await fetch(PORTAL_BRIDGE_URL, {
      method: 'GET',
      credentials: 'include',
    });
    if (!r.ok) return false;
    const data = await r.json();
    if (!data.ok || !data.session_token) return false;
    saveSession({
      token: data.session_token,
      email: data.email,
      expiresAt: data.expires_at,
    });
    return true;
  } catch {
    return false;
  }
}

export function loadSession(): StoredSession | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredSession;
    if (!parsed.token || !parsed.expiresAt) return null;
    if (new Date(parsed.expiresAt).getTime() < Date.now()) {
      localStorage.removeItem(SESSION_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function saveSession(s: StoredSession): void {
  localStorage.setItem(SESSION_KEY, JSON.stringify(s));
}

export function clearSession(): void {
  localStorage.removeItem(SESSION_KEY);
}

async function request(
  method: string,
  path: string,
  opts: { body?: unknown; auth?: boolean } = {},
): Promise<any> {
  const headers: Record<string, string> = {};
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
  if (opts.auth) {
    const session = loadSession();
    if (!session) throw new Error('Not authenticated');
    headers['Authorization'] = `Bearer ${session.token}`;
  }
  const r = await fetch(`${WORKER_BASE_URL}${path}`, {
    method,
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
  });
  const text = await r.text();
  let data: any = null;
  try { data = text ? JSON.parse(text) : null; } catch { /* keep null */ }
  if (!r.ok) {
    const err = new Error(data?.error || `HTTP ${r.status}`);
    (err as any).status = r.status;
    throw err;
  }
  return data;
}

export const api = {
  // Auth
  requestMagicLink: (email: string) =>
    request('POST', '/api/admin/auth/request-link', { body: { email } }),
  verifyMagicLink: (token: string) =>
    request('GET', `/api/admin/auth/verify?token=${encodeURIComponent(token)}`),
  logout: () => request('POST', '/api/admin/auth/logout', { auth: true }),
  me: () => request('GET', '/api/admin/me', { auth: true }),

  // Dashboard / Queue / Session
  dashboard: () => request('GET', '/api/admin/dashboard', { auth: true }),
  queue: (params: { status?: string; path?: string; industry?: string; sort?: string; limit?: number } = {}) => {
    const qs = new URLSearchParams();
    if (params.status) qs.set('status', params.status);
    if (params.path) qs.set('path', params.path);
    if (params.industry) qs.set('industry', params.industry);
    if (params.sort) qs.set('sort', params.sort);
    if (params.limit) qs.set('limit', String(params.limit));
    return request('GET', `/api/admin/queue${qs.toString() ? '?' + qs.toString() : ''}`, { auth: true });
  },
  session: (id: string) => request('GET', `/api/admin/session/${encodeURIComponent(id)}`, { auth: true }),
  patchSynthesis: (id: string, synthesis_text: string) =>
    request('PATCH', `/api/admin/session/${encodeURIComponent(id)}/synthesis`, { auth: true, body: { synthesis_text } }),
  approveSession: (id: string) =>
    request('POST', `/api/admin/session/${encodeURIComponent(id)}/approve`, { auth: true, body: {} }),
  rejectSession: (id: string, internal_note?: string) =>
    request('POST', `/api/admin/session/${encodeURIComponent(id)}/reject`, { auth: true, body: { internal_note: internal_note || null } }),
  regenerateSession: (id: string) =>
    request('POST', `/api/admin/session/${encodeURIComponent(id)}/regenerate`, { auth: true, body: {} }),

  // Phase D
  delivered: (params: { version?: string; industry?: string; path?: string; limit?: number } = {}) => {
    const qs = new URLSearchParams();
    if (params.version) qs.set('version', params.version);
    if (params.industry) qs.set('industry', params.industry);
    if (params.path) qs.set('path', params.path);
    if (params.limit) qs.set('limit', String(params.limit));
    return request('GET', `/api/admin/delivered${qs.toString() ? '?' + qs.toString() : ''}`, { auth: true });
  },
  audit: (params: { event?: string; actor?: string; session_id?: string; since?: string; limit?: number } = {}) => {
    const qs = new URLSearchParams();
    if (params.event) qs.set('event', params.event);
    if (params.actor) qs.set('actor', params.actor);
    if (params.session_id) qs.set('session_id', params.session_id);
    if (params.since) qs.set('since', params.since);
    if (params.limit) qs.set('limit', String(params.limit));
    return request('GET', `/api/admin/audit${qs.toString() ? '?' + qs.toString() : ''}`, { auth: true });
  },
  getSettings: () => request('GET', '/api/admin/settings', { auth: true }),
  patchSettings: (patch: Record<string, any>) =>
    request('PATCH', '/api/admin/settings', { auth: true, body: patch }),
  generateDemo: (label?: string, expires_hours?: number) =>
    request('POST', '/api/admin/demo/generate', { auth: true, body: { label, expires_hours } }),
  listDemo: () => request('GET', '/api/admin/demo/list', { auth: true }),
  revokeDemo: (token: string) =>
    request('DELETE', `/api/admin/demo/${encodeURIComponent(token)}`, { auth: true }),

  // Multi-model synthesis runs (Task 035 / amendment-03 Phase B2)
  listSynthesisRuns: (id: string) =>
    request('GET', `/api/admin/sessions/${encodeURIComponent(id)}/synthesis-runs`, { auth: true }),
  generateSynthesisRun: (
    id: string,
    body: { preset: string; pass_1?: { provider: string; model: string }; pass_2?: { provider: string; model: string } },
  ) =>
    request('POST', `/api/admin/sessions/${encodeURIComponent(id)}/synthesis-runs`, { auth: true, body }),
  promoteSynthesisRun: (id: string, runId: string) =>
    request('PATCH', `/api/admin/sessions/${encodeURIComponent(id)}/synthesis-runs/${encodeURIComponent(runId)}/promote`, { auth: true, body: {} }),
  blendSynthesisRuns: (
    id: string,
    body: {
      parent_run_ids: [string, string];
      preset: string;
      blend_instructions: string;
      pass_2?: { provider: string; model: string };
    },
  ) =>
    request('POST', `/api/admin/sessions/${encodeURIComponent(id)}/blend`, { auth: true, body }),

  // Rosie composition Phase 5 — retroactive admin view of last 100
  // compositions, filterable; click-through shows the full event chain
  // by correlation_id. Tier-gated server-side (T1/T2/T3).
  listCompositions: (
    params: { since?: string; until?: string; specialist?: string; owner_email?: string; slug?: string; outcome?: string; limit?: number } = {},
  ) => {
    const qs = new URLSearchParams();
    if (params.since)       qs.set('since',       params.since);
    if (params.until)       qs.set('until',       params.until);
    if (params.specialist)  qs.set('specialist',  params.specialist);
    if (params.owner_email) qs.set('owner_email', params.owner_email);
    if (params.slug)        qs.set('slug',        params.slug);
    if (params.outcome)     qs.set('outcome',     params.outcome);
    if (params.limit)       qs.set('limit',       String(params.limit));
    return request('GET', `/api/admin/compositions${qs.toString() ? '?' + qs.toString() : ''}`, { auth: true });
  },
  getCompositionChain: (correlationId: string) =>
    request('GET', `/api/admin/compositions/${encodeURIComponent(correlationId)}`, { auth: true }),

  // PDF (amendment-02)
  previewPdfUrl: (id: string) =>
    `${WORKER_BASE_URL}/api/admin/session/${encodeURIComponent(id)}/preview-pdf`,
  downloadPdfUrl: (id: string) =>
    `${WORKER_BASE_URL}/api/admin/session/${encodeURIComponent(id)}/download-pdf`,
  rerenderPdf: (id: string) =>
    request('POST', `/api/admin/session/${encodeURIComponent(id)}/rerender-pdf`, { auth: true, body: {} }),
};

/**
 * Open a PDF preview in a new tab. The preview-pdf endpoint requires a
 * Bearer token via Authorization, so we fetch the bytes ourselves and
 * open them via blob URL rather than a plain window.open.
 */
export async function openPreviewPdf(sessionId: string, synthesisText?: string): Promise<void> {
  const session = loadSession();
  if (!session) throw new Error('Not authenticated');
  const r = await fetch(api.previewPdfUrl(sessionId), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session.token}`,
    },
    body: JSON.stringify(synthesisText !== undefined ? { synthesis_text: synthesisText } : {}),
  });
  if (!r.ok) {
    let detail = '';
    try { detail = JSON.stringify(await r.json()); } catch { detail = await r.text().catch(() => ''); }
    throw new Error(`Preview failed (${r.status}) ${detail}`);
  }
  const blob = await r.blob();
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank', 'noopener');
  // Best-effort cleanup; the new tab keeps the URL alive while open.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/**
 * Trigger a download of a delivered session's PDF from R2 via the
 * admin-auth proxy endpoint.
 */
export async function downloadDeliveredPdf(sessionId: string, suggestedFilename?: string): Promise<void> {
  const session = loadSession();
  if (!session) throw new Error('Not authenticated');
  const r = await fetch(api.downloadPdfUrl(sessionId), {
    headers: { 'Authorization': `Bearer ${session.token}` },
  });
  if (!r.ok) {
    let detail = '';
    try { detail = JSON.stringify(await r.json()); } catch { detail = await r.text().catch(() => ''); }
    throw new Error(`Download failed (${r.status}) ${detail}`);
  }
  const blob = await r.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = suggestedFilename || 'cadence-advisers-diagnostic.pdf';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
